import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  useAddBillingAddressMutation,
  useUpdateBillingAddressByBillingIdMutation,
} from "../../../store/Billing/billingApiSlice";
import { getCountryCode, getStatesByCountryCode } from "../../../utils/countriesState";
import {
  sanitizeInput,
  validateAddressLine,
  validateCity,
  validateCompanyName,
  validateMobileNumber,
  validatePostalCode,
  validateState,
  validateTRNNumber,
  dialCodeOf,
} from "./billingAddressFields";

/* Add or edit a billing address — one hook for both. */

export const EMPTY_ADDRESS = {
  companyName: "",
  trnNo: "",
  address_line_1: "",
  address_line_2: "",
  country: "",
  state: "",
  city: "",
  area: "",
  postal_code: "",
  mobile: "",
  country_code: "",
};

/* Transcribed: every string is sanitised and then SQL-escaped before it is
   sent. Both forms do exactly this, separately. */
export const encodeForSQL = (obj) => {
  const encoded = {};
  for (const [key, value] of Object.entries(obj)) {
    encoded[key] =
      typeof value === "string"
        ? value.replace(/\\/g, "\\\\").replace(/'/g, "''").replace(/\0/g, "\\0")
        : value;
  }
  return encoded;
};

export const billingPayload = (values, userId) =>
  encodeForSQL({
    address_line_1: sanitizeInput(values.address_line_1),
    address_line_2: sanitizeInput(values.address_line_2),
    postal_code: sanitizeInput(values.postal_code),
    companyName: sanitizeInput(values.companyName),
    trnNo: sanitizeInput(values.trnNo),
    area: sanitizeInput(values.area),
    /* Derived from the number, not read from state — see dialCodeOf. The
       stored value is the fallback so editing an address whose mobile is
       unchanged still posts what it had. */
    country_code: dialCodeOf(values.mobile) || values.country_code || "",
    /* Already validated by the phone library, so it is sent as it stands. */
    mobile: values.mobile,
    city: sanitizeInput(values.city),
    state: sanitizeInput(values.state),
    country: values.country,
    type: "individual",
    user_id: userId,
  });

/* Transcribed field for field, including which are optional and which message
   each failure carries. */
export const validateBillingAddress = (values, states = []) => {
  const errors = {};

  const line1 = validateAddressLine(values.address_line_1 || "", "Address Line 1");
  if (line1) errors.address_line_1 = line1;

  if (values.address_line_2) {
    const line2 = validateAddressLine(values.address_line_2, "Address Line 2");
    if (line2) errors.address_line_2 = line2;
  }
  if (values.area) {
    const area = validateAddressLine(values.area, "Landmark");
    if (area) errors.area = area;
  }

  const company = validateCompanyName(values.companyName || "");
  if (company) errors.companyName = company;

  const trn = validateTRNNumber(values.trnNo || "");
  if (trn) errors.trnNo = trn;

  const city = validateCity(values.city || "");
  if (city) errors.city = city;

  const mobile = validateMobileNumber(values.mobile, values.country_code);
  if (mobile) errors.mobile = mobile;

  const state = validateState(values.state, states);
  if (state) errors.state = state;

  if (!values.country || !String(values.country).trim()) {
    errors.country = "Country is required.";
  }

  const postal = validatePostalCode(values.postal_code || "", values.country);
  if (postal) errors.postal_code = postal;

  return errors;
};

/* DRF answers a rejected save with { field: ["message"] }. */
const SERVER_FIELD_ALIASES = { country_code: "mobile" };
const FORM_LEVEL_KEYS = ["non_field_errors", "detail", "error", "message"];

export const splitServerErrors = (detail) => {
  const fallback = "An error occurred. Please try again later.";
  if (!detail) return { errors: {}, apiError: fallback };
  if (typeof detail === "string") return { errors: {}, apiError: detail };

  const first = (value) =>
    Array.isArray(value) ? String(value[0] ?? "") : String(value ?? "");

  const errors = {};
  const formLevel = [];
  for (const [key, value] of Object.entries(detail)) {
    const message = first(value);
    if (!message) continue;
    if (FORM_LEVEL_KEYS.includes(key)) {
      formLevel.push(message);
      continue;
    }
    const field = SERVER_FIELD_ALIASES[key] || key;
    /* First writer wins: an alias must not overwrite a message the server sent
       for the field itself. */
    if (!errors[field]) errors[field] = message;
  }

  /* A banner only when nothing landed on a field — otherwise it repeats what
     the inputs already say. */
  const apiError = formLevel.length
    ? formLevel.join(" ")
    : Object.keys(errors).length
    ? ""
    : fallback;
  return { errors, apiError };
};

export const useBillingAddressForm = ({ address, onSaved } = {}) => {
  const { token, userInfo } = useSelector((state) => state.auth);
  const userId = userInfo?.individual?.[0]?.individualID;

  const [values, setValues] = useState(EMPTY_ADDRESS);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [states, setStates] = useState([]);

  const countries = useMemo(() => getCountryCode(), []);
  const isEdit = Boolean(address?.billing_add_ID);

  const [addBillingAddress, { isLoading: adding }] = useAddBillingAddressMutation();
  const [updateBillingAddress, { isLoading: updating }] =
    useUpdateBillingAddressByBillingIdMutation();

  /* Transcribed prefill, key for key. */
  useEffect(() => {
    if (!address) return;
    setValues({
      companyName: address.companyName || "",
      trnNo: address.trnNo || "",
      address_line_1: address.address_line_1 || "",
      address_line_2: address.address_line_2 || "",
      country: address.country || "",
      state: address.state || "",
      city: address.city || "",
      area: address.area || "",
      postal_code: address.postal_code || "",
      mobile: address.mobile || "",
      country_code: address.country_code || "",
    });
  }, [address]);

  /* The state list follows the country, as both forms do. */
  useEffect(() => {
    let live = true;
    if (!values.country) {
      setStates([]);
      return undefined;
    }
    getStatesByCountryCode(values.country)
      .then((rows) => {
        if (live) setStates(rows || []);
      })
      .catch(() => {
        /* A country with no state list is a real answer — the field simply
           stays free text, which is what validateState allows. */
        if (live) setStates([]);
      });
    return () => {
      live = false;
    };
  }, [values.country]);

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /* Transcribed: choosing a country clears the state with it, so a state from
     the previous country cannot be submitted. */
  const setCountry = (code) => {
    setValues((prev) => ({ ...prev, country: code || "", state: "" }));
    setErrors((prev) => ({ ...prev, country: undefined, state: undefined }));
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setApiError("");

    const found = validateBillingAddress(values, states);
    setErrors(found);
    if (Object.keys(found).length > 0) return false;

    const data = billingPayload(values, userId);
    const res = isEdit
      ? await updateBillingAddress({
          data: { ...data, billing_add_ID: address.billing_add_ID },
          token,
        })
      : await addBillingAddress({ data, token });

    /* Transcribed: neither mutation is unwrapped, so a 4xx RESOLVES with an
       `error` key. Closing on that is what made a rejected save look like a
       successful one. */
    if (res?.error) {
      const split = splitServerErrors(res.error?.data);
      setErrors(split.errors);
      setApiError(split.apiError);
      return false;
    }
    if (res?.data) {
      onSaved?.(res.data);
      return true;
    }
    return false;
  };

  return {
    values,
    errors,
    apiError,
    states,
    countries,
    isEdit,
    saving: adding || updating,
    setField,
    setCountry,
    submit,
  };
};

export default useBillingAddressForm;
